/**
 * grass.ts — Where the parks are, and what the dog does about them.
 *
 * Grass used to be scenery: a green patch that made the town look less paved.
 * It is now **load-bearing** — a dog will only do its business on grass, so a
 * level without any is a level that can never be finished, and the walk itself
 * is partly about getting the dog to a park.
 *
 * That promotion is why this is its own pure module rather than a couple of
 * lines inside `Dog.ts`. The rules are small but they are consulted by the dog
 * (may I go here?), by the level generator (is there anywhere to go?), and by
 * the validator (can the walker reach it?) — and three copies of "is this point
 * on a park" is how they drift apart.
 */
import { pointInAnyRect, type Rect } from './level';

/** A point on the ground plane. */
export interface Spot {
  x: number;
  y: number;
}

/**
 * Whether a point is on a park.
 *
 * `margin` **shrinks** the patch rather than growing it: the dog has to be
 * properly on the grass, not standing on the kerb with a paw over the line.
 * Poop landing half on the pavement would look like a bug even though the
 * centre point passed.
 */
export function onGrass(x: number, y: number, parks: readonly Rect[], margin = 0): boolean {
  return pointInAnyRect(x, y, parks, -margin);
}

/**
 * The nearest point on the nearest park — where a dog that needs to go should
 * head for.
 *
 * The nearest point *on the rect*, not its centre. Aiming at the centre walks
 * the dog past the near edge and out into the middle of the green for no
 * reason, and on a big park that is a long detour for something it could have
 * done at the kerb. Clamping to the rect means a dog already on the grass gets
 * its own position back, so it stops rather than crossing to a far edge.
 *
 * Returns `null` when the level has no grass at all — which the validator
 * refuses to let happen, but a caller shouldn't have to assume that.
 */
export function nearestGrass(from: Spot, parks: readonly Rect[], margin = 0): Spot | null {
  let best: Spot | null = null;
  let bestDistSq = Infinity;

  for (const park of parks) {
    // Inset by the same margin `onGrass` demands, so the dog aims at somewhere
    // that will actually count as being on the grass once it arrives.
    const inset = Math.min(margin, park.w / 2, park.h / 2);
    const x = clamp(from.x, park.x + inset, park.x + park.w - inset);
    const y = clamp(from.y, park.y + inset, park.y + park.h - inset);
    const distSq = (x - from.x) ** 2 + (y - from.y) ** 2;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = { x, y };
    }
  }
  return best;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * How far onto the grass the dog must be before it will go.
 *
 * A dog's collision radius, near enough: it puts the whole animal on the green
 * rather than its centre point, which is what stops the poop being drawn half
 * on the paving.
 */
export const GRASS_MARGIN = 14;
